import { Spin } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { PermissionName } from '@barahpay/shared';
import { useAuth } from './AuthContext';
import { NotPermitted } from '../components/Feedback';

/** Blocks the whole shell until a session is known, then requires a signed-in user. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, restoring } = useAuth();
  const location = useLocation();

  if (restoring) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * Route-level permission gate. This is UX only — every request is authorised again on the server
 * (spec section 65).
 */
export function RequirePermission({
  permissions,
  children,
}: {
  permissions: PermissionName[];
  children: ReactNode;
}) {
  const { canAny } = useAuth();

  return canAny(permissions) ? <>{children}</> : <NotPermitted />;
}
